import axios from 'src/store/vue-axios/axios.js'
import GlobalPrototypes from '@/config/services/globalPrototypes.ts'

export default class Classbooks {
  static CLASSBOOK_TIMEOUT = import.meta.env.VITE_CLASSBOOK_EXTENDED_REQUEST_TIMEOUT

  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // API ENDPOINTS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  // CLASSBOOKS -> /classbooks/${classbookId}*
  static classbooksUrl = (classbookId) => `/classbooks/${classbookId}`

  // ABSENCES -> /classbooks/${classbookId}/absences*
  static absencesUrl = (classbookId) => `/classbooks/${classbookId}/absences`
  static absencesUsersUrl = (classbookId) => `/classbooks/${classbookId}/absences/users`
  static absencesUsersByIdUrl = (classbookId, userId) => `/classbooks/${classbookId}/absences/users/${userId}`
  static absencesSickWithDelPaginatedSearchUrl = (classbookId, page, searchVal, filter, order) => `/classbooks/${classbookId}/absences/sick?with_deleted=1&page=${page}&search=${searchVal}&filter=${filter}&order_by=${order}`
  static absencesByEntityNameEntityIdUrl = (classbookId, entityName, entityId) => `/classbooks/${classbookId}/absences/${entityName}/${entityId}`
  static absenceTypesByEntityNameEntityIdUrl = (classbookId, entityName, entityId) => `/classbooks/${classbookId}/absences/${entityName}/${entityId}/absences/types`
  static absenceTypesUrl = (classbookId) => `/classbooks/${classbookId}/absences/types`
  static absenceCategoriesUrl = (classbookId) => `/classbooks/${classbookId}/absences/types/groups`
  static absenceTypesByIdUrl = (classbookId, absenceId) => `/classbooks/${classbookId}/absences/types/${absenceId}`
  static absenceNotificationUrl = (classbookId) => `/classbooks/${classbookId}/absences/notify-users`

  // ABSENCES BY ID -> /classbooks/${classbookId}/absences/${absenceId}*
  static absencesByIdUrl = (classbookId, absenceId) => `/classbooks/${classbookId}/absences/${absenceId}`
  static absencesHistoryByIdUrl = (classbookId, absenceId) => `/classbooks/${classbookId}/absences/${absenceId}/history`

  // NOTICES -> /classbooks/${classbookId}/notices*
  static noticesUrl = (classbookId) => `/classbooks/${classbookId}/notices`
  static noticesUsersByIdUrl = (classbookId, userId) => `/classbooks/${classbookId}/notices/users/${userId}`
  static noticesByIdUrl = (classbookId, noticeId) => `/classbooks/${classbookId}/notices/${noticeId}`
  static noticesHistoryByIdUrl = (classbookId, noticeId) => `/classbooks/${classbookId}/notices/${noticeId}/history`
  static noticesCategoriesUrl = (classbookId) => `/classbooks/${classbookId}/notices/categories`
  static noticesGradesByIdUrl = (classbookId, gradeId) => `/classbooks/${classbookId}/notices/grades/${gradeId}`
  static noticesByEntryIdUrl = (classbookId, entryId) => `/classbooks/${classbookId}/notices/users?entry_id=${entryId}`
  static noticesByEntityNameEntityIdUrl = (classbookId, entityName, entityId) => `/classbooks/${classbookId}/notices/${entityName}/${entityId}`

  // HOMEWORKS -> /classbooks/${classbookId}/homeworks*
  static homeworksUrl = (classbookId) => `/classbooks/${classbookId}/homeworks`
  static homeworksByIdUrl = (classbookId, homeworkId) => `/classbooks/${classbookId}/homeworks/${homeworkId}`
  static homeworksByEntityNameEntityIdUrl = (classbookId, entityName, entityId) => `/classbooks/${classbookId}/homeworks/${entityName}/${entityId}`
  static homeworksByGradeAndIdUrl = (classbookId, gradeId) => `/classbooks/${classbookId}/homeworks/grades/${gradeId}`
  static homeworksHistoryByIdUrl = (classbookId, homeworkId) => `/classbooks/${classbookId}/homeworks/${homeworkId}/history`

  // GRADES -> /classbooks/${classbookId}/grades*
  static gradesByIdUrl = (classbookId, gradeId) => {
    if (gradeId) return `/classbooks/${classbookId}/grades/${gradeId}`
    return `/classbooks/${classbookId}/grades`
  }

  // COURSES -> /classbooks/${classbookId}/courses*
  static coursesByIdUrl = (classbookId, courseId) => {
    if (courseId) return `/classbooks/${classbookId}/courses/${courseId}`
    return `/classbooks/${classbookId}/courses`
  }

  // USERS -> /classbooks/${classbookId}/users*
  static usersByIdUrl = (classbookId, userId) => {
    if (userId) return `/classbooks/${classbookId}/users/${userId}`
    return `/classbooks/${classbookId}/users`
  }
  static statisticsByByUserIdUrl = (classbookId, userId) => `/classbooks/${classbookId}/users/${userId}/statistics`

  // ENTRIES -> /classbooks/entries/*
  static entriesByIdUrl = (entryId) => `/classbooks/entries/${entryId}`
  static entriesTopicByIdUrl = (entryId) => `/classbooks/entries/${entryId}/topic`
  static entriesTopicHistoryByIdUrl = (entryId) => `/classbooks/entries/${entryId}/topic/history`
  static entriesByIdUsersUrl = (entryId) => `/classbooks/entries/${entryId}/users`
  static entriesByIdUsersByIdUrl = (entryId, userId) => `/classbooks/entries/${entryId}/users/${userId}`
  static entriesAbsentByIdUsersByIdUrl = (entryId, userId) => `/classbooks/entries/${entryId}/users/${userId}/absent`
  static entriesAbsentTooLateByIdUsersByIdUrl = (entryId, userId) => `/classbooks/entries/${entryId}/users/${userId}/absent/too-late`
  static entriesAbsentRemoveByIdUsersByIdUrl = (entryId, userId) => `/classbooks/entries/${entryId}/users/${userId}/absent/remove`
  static entriesUsersByIdPaginatedSearchUrl = (entryId, page, searchVal) => `/classbooks/entries/${entryId}/users?page=${page}&search=${searchVal}`

  // ENTRIES -> /classbooks/${classbookId}/entries*
  static entriesByClassbookIdUrl = (classbookId) => `/classbooks/${classbookId}/entries`
  static entriesByClassbookIdByEntityIdUrl = (classbookId, entityName, entityId) => `/classbooks/${classbookId}/entries/${entityName}/${entityId}`
  static entriesByClassbookIdByEntityPaginatedSearchUrl = (classbookId, entityName, page, searchVal) => `/classbooks/${classbookId}/${entityName}?search=${searchVal}&page=${page}`

  // PDF EXPORT
  static generatePdfExportUrl = (classbookId, gradeId, beginsAt, endsAt) => `/classbooks/${classbookId}/grades/${gradeId}/entries/export/generate-download-link?begins_at=${beginsAt}&ends_at=${endsAt}`

  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // API IMPLEMENTATIONS ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
  // ++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

  // CLASSBOOKS -> /classbooks/${classbookId}*
  static async getClassbooks (classbookId) {
    return axios.get(this.classbooksUrl(classbookId))
  }

  // ABSENCES -> /classbooks/${classbookId}/absences*
  static async putAbsences (classbookId, payload) {
    return axios.put(this.absencesUrl(classbookId), payload)
  }

  static async postAbsences (classbookId, payload) {
    return axios.post(this.absencesUrl(classbookId), payload)
  }

  static async getAbsencesUsersById (classbookId, userId, config) {
    return axios.get(this.absencesUsersByIdUrl(classbookId, userId), { ...config, timeout: this.CLASSBOOK_TIMEOUT })
  }

  static async getAbsencesSickWithDelPaginatedSearch (classbookId, page, searchVal, filter = '', order = '') {
    return axios.get(this.absencesSickWithDelPaginatedSearchUrl(classbookId, page, searchVal, filter, order), { timeout: this.CLASSBOOK_TIMEOUT })
  }

  static async getAbsencesByEntityNameEntityId (classbookId, entityName, entityId, config, customUrlParams = '') {
    return axios.get(`${this.absencesByEntityNameEntityIdUrl(classbookId, entityName, entityId) + customUrlParams}`, { ...config, timeout: this.CLASSBOOK_TIMEOUT })
  }

  static async getAbsenceTypesByEntityNameEntityId (classbookId, entityName, entityId) {
    return axios.get(this.absenceTypesByEntityNameEntityIdUrl(classbookId, entityName, entityId))
  }

  static async getAbsenceCategories (classbookId) {
    return axios.get(this.absenceCategoriesUrl(classbookId))
  }

  static async getAbsenceTypes (classbookId) {
    return axios.get(this.absenceTypesUrl(classbookId))
  }

  static async postAbsenceTypes (classbookId, payload) {
    return axios.post(this.absenceTypesUrl(classbookId), payload)
  }

  static async putAbsenceTypesById (classbookId, absenceId, payload) {
    return axios.put(this.absenceTypesByIdUrl(classbookId, absenceId), payload)
  }

  static async deleteAbsenceTypesById (classbookId, absenceId) {
    return axios.delete(this.absenceTypesByIdUrl(classbookId, absenceId))
  }

  static async postAbsenceNotification (classbookId, payload) {
    return axios.post(this.absenceNotificationUrl(classbookId), payload)
  }

  // ABSENCES BY ID -> /classbooks/${classbookId}/absences/${absenceId}*
  static async getAbsencesById (classbookId, absenceId) {
    return axios.get(this.absencesByIdUrl(classbookId, absenceId))
  }

  static async putAbsencesById (classbookId, absenceId, payload) {
    return axios.put(this.absencesByIdUrl(classbookId, absenceId), payload)
  }

  static async deleteAbsencesById (classbookId, absenceId) {
    return axios.delete(this.absencesByIdUrl(classbookId, absenceId))
  }

  static async getAbsencesHistoryById (classbookId, absenceId) {
    return axios.get(this.absencesHistoryByIdUrl(classbookId, absenceId))
  }

  // NOTICES -> /classbooks/${classbookId}/notices*
  static async postNotices (classbookId, payload) {
    return axios.post(this.noticesUrl(classbookId), payload)
  }

  static async getNoticesUsersById (classbookId, userId, config) {
    return axios.get(this.noticesUsersByIdUrl(classbookId, userId), { ...config, timeout: this.CLASSBOOK_TIMEOUT })
  }

  static async putNoticesById (classbookId, noticeId, payload) {
    return axios.put(this.noticesByIdUrl(classbookId, noticeId), payload)
  }

  static async deleteNoticesById (classbookId, noticeId) {
    return axios.delete(this.noticesByIdUrl(classbookId, noticeId))
  }

  static async getNoticesHistoryById (classbookId, noticeId) {
    return axios.get(this.noticesHistoryByIdUrl(classbookId, noticeId))
  }

  static async getNoticesCategories (classbookId) {
    return axios.get(this.noticesCategoriesUrl(classbookId))
  }

  static async getNoticesByEntryId (classbookId, entryId) {
    return axios.get(this.noticesByEntryIdUrl(classbookId, entryId))
  }

  // HOMEWORKS -> /classbooks/${classbookId}/homeworks*
  static async putHomeworksById (classbookId, homeworkId, payload) {
    return axios.put(this.homeworksByIdUrl(classbookId, homeworkId), payload)
  }

  static async postHomeworks (classbookId, payload) {
    return axios.post(this.homeworksUrl(classbookId), payload)
  }

  static async deleteHomeworksById (classbookId, homeworkId) {
    return axios.delete(this.homeworksByIdUrl(classbookId, homeworkId))
  }

  static async getHomeworksHistoryById (classbookId, homeworkId) {
    return axios.get(this.homeworksHistoryByIdUrl(classbookId, homeworkId))
  }

  static async getHomeworksByGradeId (classbookId, gradeId, config = {}) {
    return axios.get(this.homeworksByGradeAndIdUrl(classbookId, gradeId), config)
  }

  // USERS -> /classbooks/${classbookId}/users*
  static async getStatisticsByByUserIdUrl (classbookId, userId) {
    return axios.get(this.statisticsByByUserIdUrl(classbookId, userId), { timeout: this.CLASSBOOK_TIMEOUT })
  }

  // ENTRIES -> /classbooks/entries/*
  static async getEntriesById (entryId) {
    return axios.get(this.entriesByIdUrl(entryId))
  }

  static async postEntriesTopicById (entryId, payload) {
    return axios.post(this.entriesTopicByIdUrl(entryId), payload)
  }

  static async getEntriesTopicHistoryById (entryId) {
    return axios.get(this.entriesTopicHistoryByIdUrl(entryId))
  }

  static async putEntriesById (entryId, payload) {
    return axios.put(this.entriesByIdUrl(entryId), payload)
  }

  static async getEntriesUsersByIdPaginatedSearch (entryId, page, searchVal) {
    return axios.get(this.entriesUsersByIdPaginatedSearchUrl(entryId, page, searchVal))
  }

  static async putEntriesByIdUsersById (entryId, userId, payload, config) {
    return axios.put(this.entriesByIdUsersByIdUrl(entryId, userId), payload, config)
  }

  static async postEntriesAbsentByIdUsersById (entryId, userId, payload) {
    return axios.post(this.entriesAbsentByIdUsersByIdUrl(entryId, userId), payload)
  }

  static async putEntriesAbsentTooLateByIdUsersById (entryId, userId, payload) {
    return axios.put(this.entriesAbsentTooLateByIdUsersByIdUrl(entryId, userId), payload)
  }

  static async putEntriesAbsentRemoveByIdUsersById (entryId, userId) {
    return axios.put(this.entriesAbsentRemoveByIdUsersByIdUrl(entryId, userId))
  }

  static async getEntriesByIdUsersById (entryId, userId) {
    return axios.get(this.entriesByIdUsersByIdUrl(entryId, userId))
  }

  static async getEntriesByClassbookIdByEntityId (classbookId, entityName, entityId, customUrlParams = '') {
    return GlobalPrototypes.cacheHttp(`${this.entriesByClassbookIdByEntityIdUrl(classbookId, entityName, entityId) + customUrlParams}`)
  }

  // ENTRIES -> /classbooks/${classbookId}/entries*
  static async getEntriesByClassbookIdByEntityPaginatedSearch (classbookId, entityName, page, searchVal) {
    // as this data doesn´t update so often, we can show the cache and update in background
    return GlobalPrototypes.cacheHttp(this.entriesByClassbookIdByEntityPaginatedSearchUrl(classbookId, entityName, page, searchVal), { timeout: this.CLASSBOOK_TIMEOUT })
  }

  // PDF EXPORT
  static async postGeneratePdfExport (classbookId, gradeId, beginsAt, endsAt) {
    return axios.post(this.generatePdfExportUrl(classbookId, gradeId, beginsAt, endsAt))
  }
}
